/* eslint-disable react-refresh/only-export-components */
import { Children, isValidElement, type ReactNode } from "react";

import { cn, platformProps } from "../../lib";
import { SidebarMenuContext } from "../../molecules/sidebar-nav-item/context";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "./sidebar";
import type { AppSidebarProps } from "./types";

/**
 * AppSidebar — Web (the shadcn shell that hosts `SidebarMenu`).
 *
 * Composes the co-located vendored `./sidebar` block (`Sidebar` + header /
 * content / footer / rail) re-tokenized to Luce. It surfaces shadcn's whole
 * state machine — `SidebarProvider` cookie persistence, `Cmd/Ctrl+B`, the
 * automatic mobile `Sheet` — and re-exports `SidebarProvider`, `SidebarTrigger`
 * and `useSidebar` so consumers import everything from `@luce/ui`.
 *
 * Collapse bridge: it reads `useSidebar().state` and threads the resulting
 * `collapsed` flag into `SidebarMenuContext`, so every descendant
 * `SidebarNavItem` collapses to an icon chip in lock-step with the shell. The
 * `SidebarMenu` organism stays self-contained — it never imports from here, it
 * just inherits the ambient flag.
 *
 * Native is a permanent no-op (see `app-sidebar.native.tsx`) — this file is the
 * web half of that cross-platform pair.
 *
 * @see docs/tickets/DS-042-sidebar-menu.md
 */

const AppSidebarHeader = ({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) => <SidebarHeader className={className}>{children}</SidebarHeader>;
AppSidebarHeader.displayName = "AppSidebar.Header";

const AppSidebarFooter = ({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) => <SidebarFooter className={className}>{children}</SidebarFooter>;
AppSidebarFooter.displayName = "AppSidebar.Footer";

const isSlot =
  (displayName: string) =>
  (child: ReactNode): boolean =>
    isValidElement(child) && (child.type as { displayName?: string })?.displayName === displayName;

const AppSidebarRoot = ({
  collapsible = "icon",
  side = "left",
  variant = "sidebar",
  children,
  className,
  testID,
}: AppSidebarProps) => {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  // Partition the slots so the shell can stack them in the canonical order:
  // header (top) → SidebarMenu in a scrollable SidebarContent (middle, flex-1)
  // → footer (bottom). Everything that isn't a Header/Footer slot is content.
  const childArray = Children.toArray(children);
  const header = childArray.find(isSlot("AppSidebar.Header"));
  const footer = childArray.find(isSlot("AppSidebar.Footer"));
  const content = childArray.filter((child) => child !== header && child !== footer);

  return (
    <SidebarMenuContext.Provider value={{ collapsed }}>
      <Sidebar
        collapsible={collapsible}
        side={side}
        variant={variant}
        className={cn(className)}
        {...platformProps({ testID })}
        aria-label="Main navigation"
      >
        {header}
        <SidebarContent>{content}</SidebarContent>
        {footer}
        <SidebarRail />
      </Sidebar>
    </SidebarMenuContext.Provider>
  );
};
AppSidebarRoot.displayName = "AppSidebar";

/**
 * `AppSidebar` with `Header` / `Footer` slots — mirrors the native no-op's
 * compound API so `AppSidebar.Header` / `AppSidebar.Footer` resolve identically
 * on both platforms.
 */
const AppSidebar = Object.assign(AppSidebarRoot, {
  Header: AppSidebarHeader,
  Footer: AppSidebarFooter,
});

export { AppSidebar, SidebarProvider, SidebarTrigger, useSidebar };
